/** Compact inline editor for an action's editable config values (`fields()`).
 *
 * The single surface that renders inputs for all 58 preflight actions, in three
 * hosts (the Preflight tab, the Items drawer, the wizard's Parameters step), so
 * anything learned here lifts every item at once.
 *
 * Renders each field via the InlineEditField widget family — a value shown as
 * clickable text (read mode) that swaps to an input on click. Edits accumulate as
 * *pending* (rendered distinctly) in local state; **Save values** persists only the
 * changed fields via the `configure` endpoint (overwrite), **Discard** reverts them.
 * Manage-gated: viewers see values but can't edit or save. Backend is authoritative.
 *
 * What the API sends and this renders (the audit's F4): `required` as a marker
 * plus a warning treatment while empty; each `missing_inputs` entry under the
 * field it names, matched on `key`, rather than concatenated into a tooltip on a
 * different screen; `default` for every type including bool and list, with a
 * one-click reset; and the per-key `{errors: {key: message}}` that `configure`
 * answers with, anchored to the field that caused it instead of a bare snackbar.
 *
 * Sections and value warnings (`fieldPresentation`): fields a group rule claims (the
 * check-in questions, the catalogue automation answers) follow the item's own fields under
 * a heading, and a value whose consequence deserves it (AI tours off) carries a warning on
 * the field. A bool with nothing stored shows the default that applies, marked as such.
 *
 * `widget` (DD3) is the escape hatch for a value whose `type` understates it —
 * the same vocabulary the integrations config editor speaks through `x-widget`.
 * An unknown or absent widget falls through to the plain `type`, so the backend
 * can name a control this build doesn't have yet without breaking the field.
 *
 * Concurrency: the server value of a field can change underneath the operator — a
 * run writes config, or the wizard's 4s poll lands mid-edit. This editor keeps the
 * operator's edits and the server values apart (`vals` vs `baseline`) so a refetch
 * can rebase the fields nobody touched without discarding the one being typed into.
 * Both earlier behaviours were wrong in opposite directions: the Preflight tab never
 * rebased, so post-run values showed as pending edits and Save wrote the stale ones
 * back over them; the wizard remounted the whole editor on any server change, which
 * threw away in-flight typing.
 */
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { PreflightActionField, PreflightMissingInput } from '@/api/preflight';
import { fieldErrorsOf, items as itemsApi } from '@/api/preflight';
import { HelpLink } from '@/components/HelpLink';
import { FieldBlock, InlineEditField } from '@/components/InlineEdit/InlineEditField';
import type { InlineEditOption, InlineEditValue } from '@/components/InlineEdit/InlineEditField';
import {
  ImportSourceWidget,
  UserKeyCell,
} from '@/features/tour-operators/integrations/config-widgets';
import type { HelpStoryId } from '@/help/registry.gen';

import { fieldSections, fieldWarning } from './fieldPresentation';

/** A bool nothing is stored for stands on its default, when it has one — the convention
 *  a text field follows with its greyed placeholder. Rendering it as Disabled read as a
 *  decision nobody made: the catalogue questions arrive unanswered with the value that
 *  applies (AI tours on) as their default, and "Disabled" there said the opposite. */
function isUnsetBool(f: PreflightActionField): boolean {
  return f.type === 'bool' && f.value == null && typeof f.default === 'boolean';
}

function toInput(f: PreflightActionField): InlineEditValue {
  return isUnsetBool(f) ? (f.default as boolean) : coerce(f, f.value);
}

/** Widgets whose value is a JSON structure rather than a scalar.
 *
 *  `InlineEditValue` has no object member, and it should not grow one for a
 *  single field: everything this editor does — dirty tracking, rebase, conflict
 *  detection, Discard — compares values as strings already. So a structured
 *  value travels through here as its JSON TEXT and is parsed back in
 *  `serialize`, which keeps one code path for all 58 actions. `''` stays `''`,
 *  so an unset credential still reads as empty to the required marker.
 *
 *  FareHarbor's `user_key` is the case: a bare key string OR an alias-keyed
 *  object. Stringifying that object the way a text field does produced
 *  `"[object Object]"` — the shape the plain text box invited operators to
 *  destroy it with. */
const STRUCTURED_WIDGETS = new Set(['user-key']);

const isStructured = (f: PreflightActionField) => STRUCTURED_WIDGETS.has(f.widget);

function encodeJson(v: unknown): string {
  return v == null || v === '' ? '' : JSON.stringify(v);
}

function decodeJson(v: InlineEditValue): unknown {
  if (typeof v !== 'string' || v === '') return '';
  try {
    return JSON.parse(v) as unknown;
  } catch {
    // Not something we wrote. Hand back what the operator can see rather than
    // silently dropping their value.
    return v;
  }
}

/** Any JSON value from the API → the input shape this field's control expects. */
function coerce(f: PreflightActionField, v: unknown): InlineEditValue {
  if (f.type === 'bool') return typeof v === 'boolean' ? v : false;
  if (f.type === 'list') return Array.isArray(v) ? v.map(String) : [];
  if (isStructured(f)) return encodeJson(v);
  return v != null ? String(v) : '';
}

function serialize(f: PreflightActionField, v: InlineEditValue): unknown {
  if (f.type === 'bool') return Boolean(v);
  if (f.type === 'list') return Array.isArray(v) ? v : [];
  if (isStructured(f)) return decodeJson(v);
  if (f.type === 'int') return v === '' ? null : v; // backend coerces "7" → 7; "" clears
  return v; // text / email / select
}

/** What the copy button puts on the clipboard: the value itself, never its
 *  transport encoding — a single key pastes into the vendor dashboard bare, not
 *  wrapped in the quotes `encodeJson` added. */
function clipboardText(f: PreflightActionField, v: InlineEditValue): string {
  if (!isStructured(f)) return String(v);
  const raw = decodeJson(v);
  return typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
}

const isEmpty = (v: InlineEditValue) => v === '' || (Array.isArray(v) && v.length === 0);

function toOptions(f: PreflightActionField): InlineEditOption[] | undefined {
  if (f.type !== 'select' || !f.options) return undefined;
  return f.options.map((o) => {
    const rec = o as Record<string, unknown>;
    const value = String(rec.value ?? '');
    return { value, label: String(rec.label ?? value) };
  });
}

/** The platform default, in the same words the control uses for a value.
 *
 *  A credential has no useful default line: the API masks `value` and `default`
 *  alike, so it would read "Default: ••••••••" and offer a Reset that swaps one
 *  mask for another. */
function formatDefault(f: PreflightActionField): string {
  if (f.secret || f.default == null) return '';
  // A structured default has no one-line form — `String({})` is "[object Object]".
  if (typeof f.default === 'object' && !Array.isArray(f.default)) return '';
  if (f.type === 'bool') return f.default ? 'Enabled' : 'Disabled';
  if (f.type === 'list') {
    const arr = Array.isArray(f.default) ? f.default.map(String) : [];
    return arr.length ? arr.join(', ') : '(none)';
  }
  if (f.type === 'select') {
    const opt = (toOptions(f) ?? []).find((o) => o.value === String(f.default));
    return opt ? opt.label : String(f.default);
  }
  return String(f.default);
}

/** Units the backend writes into int labels as a trailing parenthetical. */
const UNIT_WORDS = [
  'min',
  'mins',
  'minutes',
  'hr',
  'hrs',
  'hours',
  'day',
  'days',
  'sec',
  'seconds',
];

/** `"End delta (hrs after start)"` → `["End delta", "hrs after start"]`.
 *
 *  The unit belongs beside the number, not inside the label: it reads as a
 *  sentence there, and it ends the `"(min)"` / `"(hrs after)"` inconsistency the
 *  backend labels carry without needing all fifteen of them renamed. Only a
 *  parenthetical that genuinely opens with a unit is moved. */
function splitUnit(f: PreflightActionField): [string, string | undefined] {
  if (f.type !== 'int') return [f.label, undefined];
  const m = /^(.*?)\s*\(([^()]+)\)$/.exec(f.label);
  if (!m || !m[1] || !m[2]) return [f.label, undefined];
  const first = m[2].split(/\s+/)[0]?.toLowerCase() ?? '';
  return UNIT_WORDS.includes(first) ? [m[1], m[2]] : [f.label, undefined];
}

const BARE_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True for an address the platform will actually accept.
 *
 *  RFC 5322 allows a display name around the address — `Aurora <ops@aurora.com>`
 *  — and that is the form the backend's own defaults use: `EMAIL_CONFIG` seeds
 *  `"<code> <assistant@curatedplanet.com>"`. The first version of this check
 *  took the bare form only, so the editor marked the platform's own generated
 *  default "Not a valid email address" on every newly created tenant.
 */
export function isEmailValue(v: string): boolean {
  const trimmed = v.trim();
  const named = /^[^<>]*<([^<>]+)>$/.exec(trimmed);
  return BARE_ADDRESS.test((named?.[1] ?? trimmed).trim());
}

/** Client-side check, so an obvious mistake never costs a round trip. The
 *  backend re-validates and stays authoritative. */
function validate(f: PreflightActionField, v: InlineEditValue): string | undefined {
  if (typeof v !== 'string' || v === '') return undefined;
  if (f.type === 'int' && !/^-?\d+$/.test(v.trim())) return 'Whole number, e.g. 120.';
  if (f.type === 'email' && !isEmailValue(v)) return 'Not a valid email address.';
  return undefined;
}

/** What the CONTROL owes the operator on top of the backend's own `help`.
 *
 *  The schema description says what a `user_key` is; it does not say that the
 *  alias is what product sync walks, which is the whole reason a bare key is the
 *  wrong shape for a tenant with more than one FareHarbor company. */
const WIDGET_HELP: Record<string, string> = {
  'user-key':
    'Each alias is the FareHarbor company shortname. Product sync picks a company from these aliases, so a bare key leaves it with nothing to sync.',
};

function helpText(f: PreflightActionField): string | undefined {
  const parts = [f.help, WIDGET_HELP[f.widget]];
  // A masked credential looks like a value but cannot be read back, so say what
  // typing over it does before the operator does it.
  if (f.secret) {
    parts.push('Stored value is hidden — type to replace it; leave it alone to keep it.');
  }
  return parts.filter(Boolean).join(' ') || undefined;
}

/** DD3 — dispatch a field to its richer control by `widget`, mirroring the
 *  integrations config editor's `integrationWidget`. `null` means "no such
 *  widget here": the caller falls back to the plain `type`, so the backend can
 *  name a control this build doesn't have yet without breaking the field. */
function widgetControl(
  f: PreflightActionField,
  value: InlineEditValue,
  args: { topId: number; readOnly: boolean; onChange: (v: InlineEditValue) => void },
): ReactNode | null {
  switch (f.widget) {
    case 'import-source': {
      // Show the DEFAULT when nothing is stored yet. A plain field renders its
      // default as greyed placeholder text; a picker has no such mode, so an
      // unset `source_id` rendered as an empty dropdown — on an item the wizard
      // simultaneously reported as ready, because the default satisfies it. The
      // operator saw "required, nothing selected, step complete" and was right
      // to disbelieve it. The `Default:` line below still says whose choice it is.
      const shown = typeof value === 'string' && value ? value : String(f.default ?? '');
      return (
        <ImportSourceWidget
          toId={args.topId}
          value={shown || null}
          onChange={(code) => args.onChange(code ?? '')}
          readOnly={args.readOnly}
        />
      );
    }
    case 'user-key':
      // The integrations config editor's own dialog, not a second one: Single vs
      // By-alias, and per-alias `key` + `companies`, are already modelled there,
      // and a fork would drift from the shape the backend reads.
      return (
        <UserKeyCell
          value={decodeJson(value)}
          onChange={(next) => args.onChange(encodeJson(next))}
          readOnly={args.readOnly}
        />
      );

    default:
      return null;
  }
}

const snapshot = (fields: PreflightActionField[]): Record<string, InlineEditValue> =>
  Object.fromEntries(fields.map((f): [string, InlineEditValue] => [f.key, toInput(f)]));

const same = (a: InlineEditValue | undefined, b: InlineEditValue | undefined) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** A field whose content needs the whole row rather than one grid column: a chip
 *  list, or free text long enough that half a row would fold it. Everything else
 *  — ints, bools, selects, short text — pairs up, which is what turns
 *  NOTIFICATION_TIMING_CONFIG's eight ints from a ragged mosaic into a grid. */
function isWide(f: PreflightActionField, value: InlineEditValue): boolean {
  if (f.type === 'list') return true;
  if (f.type !== 'text' && f.type !== 'email') return false;
  return Math.max(String(value ?? '').length, formatDefault(f).length) > 28;
}

/** Required fields first (each partition keeping the action's declared order),
 *  because they are what gates the setup run. */
const requiredFirst = (fields: PreflightActionField[]) => [
  ...fields.filter((f) => f.required),
  ...fields.filter((f) => !f.required),
];

interface Props {
  fields: PreflightActionField[];
  /** The tenant — widgets that list tenant-scoped options need it. */
  topId: number;
  /** The item these fields belong to — revealing a masked value needs it. */
  itemId?: number;
  canManage: boolean;
  saving: boolean;
  /** The item's unmet inputs; entries whose `key` names a field render on it. */
  missingInputs?: PreflightMissingInput[];
  /** Resolves when the values are persisted; rejects with the API error, whose
   *  per-key `errors` are routed back to the fields that caused them. */
  onSave: (values: Record<string, unknown>) => void | Promise<unknown>;
}

export function PreflightFieldEditor({
  fields,
  topId,
  itemId,
  canManage,
  saving,
  missingInputs = [],
  onSave,
}: Props) {
  /** The operator's working copy. */
  const [vals, setVals] = useState<Record<string, InlineEditValue>>(() => snapshot(fields));
  /** The server value each key was last reconciled against. */
  const [baseline, setBaseline] = useState<Record<string, InlineEditValue>>(() => snapshot(fields));
  /** Keys the operator has edited since the last save — these are never rebased. */
  const touchedRef = useRef<Set<string>>(new Set());
  const [, forceRender] = useState(0);
  /** Per-key messages the backend returned for the last save. */
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  /** Secrets the operator has asked to see (masked again on remount), and the
   *  brief "copied" acknowledgement on the copy button. */
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  // Only re-reconcile when the SERVER values change, not on every parent render —
  // `fields` is a fresh array identity from the query on each one.
  const serverKey = JSON.stringify(fields.map((f) => [f.key, f.value]));
  useEffect(() => {
    const server = snapshot(fields);
    const touched = touchedRef.current;
    setVals((cur) => {
      const next = { ...cur };
      for (const f of fields) {
        if (!touched.has(f.key)) {
          next[f.key] = server[f.key] ?? toInput(f);
        } else if (same(server[f.key], cur[f.key])) {
          // The server now agrees with the local edit — it landed. Stop tracking it
          // as local so a later change from elsewhere can rebase this field again.
          touched.delete(f.key);
          next[f.key] = server[f.key] ?? toInput(f);
        }
      }
      return next;
    });
    setBaseline((cur) => {
      const next = { ...cur };
      for (const f of fields) {
        if (!touched.has(f.key)) next[f.key] = server[f.key] ?? toInput(f);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const setField = (key: string, v: InlineEditValue) => {
    touchedRef.current.add(key);
    // The server's verdict was about the value that produced it.
    setServerErrors((cur) => {
      if (!(key in cur)) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
    setVals((s) => ({ ...s, [key]: v }));
  };

  const valueOf = (f: PreflightActionField) => vals[f.key] ?? toInput(f);

  /** Swap a masked value for the real one, in place.
   *
   *  Written to BOTH the working copy and the baseline, so revealing is not an
   *  edit: nothing goes pending, Save stays disabled, and typing on from the real
   *  value behaves like any other field. The rebase effect keys on server values,
   *  and the mask never changes, so a poll cannot silently re-hide it either. */
  const reveal = async (f: PreflightActionField) => {
    if (itemId == null) return;
    try {
      const { value } = await itemsApi.revealField(topId, itemId, f.key);
      const real = coerce(f, value);
      setVals((cur) => ({ ...cur, [f.key]: real }));
      setBaseline((cur) => ({ ...cur, [f.key]: real }));
      setRevealed((cur) => ({ ...cur, [f.key]: true }));
    } catch {
      // Reported on the field, like every other per-key message here — a
      // snackbar would make this shared editor require a provider its three
      // hosts do not all supply.
      setServerErrors((cur) => ({ ...cur, [f.key]: 'Could not reveal the value.' }));
    }
  };

  const copy = (f: PreflightActionField) => {
    void navigator.clipboard
      ?.writeText(clipboardText(f, valueOf(f)))
      .then(() => {
        setCopied((cur) => ({ ...cur, [f.key]: true }));
        setTimeout(() => setCopied((cur) => ({ ...cur, [f.key]: false })), 1500);
      })
      .catch(() => setServerErrors((cur) => ({ ...cur, [f.key]: 'Could not copy.' })));
  };

  // Dirty = edited away from the value it was edited against.
  const dirtyKeys = fields
    .filter((f) => touchedRef.current.has(f.key) && !same(vals[f.key], baseline[f.key]))
    .map((f) => f.key);

  // Conflicted = edited locally AND changed on the server since. Naming them is the
  // point: a silent last-write-wins here would overwrite whatever the run just wrote.
  const conflicts = fields.filter(
    (f) => touchedRef.current.has(f.key) && !same(baseline[f.key], toInput(f)),
  );

  const errorOf = (f: PreflightActionField) => validate(f, valueOf(f)) ?? serverErrors[f.key];
  // Only a value about to be SENT can block the save — a field the operator has
  // not touched may hold something the backend already stored, and locking the
  // whole editor over it would strand every other field.
  const blocking = fields.filter((f) => dirtyKeys.includes(f.key) && errorOf(f) != null);

  const save = () => {
    const byKey = new Map(fields.map((f) => [f.key, f]));
    const out: Record<string, unknown> = {};
    for (const k of dirtyKeys) {
      const f = byKey.get(k);
      if (f) out[k] = serialize(f, vals[k] ?? toInput(f));
    }
    setServerErrors({});
    const sent = Object.keys(out);
    const result = onSave(out);
    if (!result || typeof (result as Promise<unknown>).then !== 'function') return;
    void (result as Promise<unknown>)
      .then(() => {
        // The write landed, so these keys are no longer unsaved edits — settle
        // them against what we sent and let the next server snapshot rebase them.
        //
        // Without this, a SECRET could never settle: the API never returns the
        // value, so the mask coming back read as "someone else changed this while
        // you were editing" and offered Take theirs — on the operator's own save,
        // one keystroke after they typed the credential.
        for (const k of sent) touchedRef.current.delete(k);
        setBaseline((cur) => {
          const next = { ...cur };
          for (const k of sent) next[k] = vals[k] ?? '';
          return next;
        });
        forceRender((n) => n + 1);
      })
      // A rejection carrying per-key messages belongs on the fields; anything
      // else is the host's to report (it already shows a snackbar).
      .catch((e: unknown) => {
        const errs = fieldErrorsOf(e);
        if (errs) setServerErrors(errs);
      });
  };

  const takeTheirs = () => {
    touchedRef.current.clear();
    setVals(snapshot(fields));
    setBaseline(snapshot(fields));
    setServerErrors({});
    forceRender((n) => n + 1);
  };

  const whyByKey = new Map(missingInputs.map((m) => [m.key, m.why]));
  // The item's own fields first, then one headed section per group that claims any
  // (`fieldPresentation`), each keeping the required-first order; a heading spans the row.
  const entries = fieldSections(requiredFirst(fields)).flatMap(
    (
      section,
    ): Array<
      { kind: 'heading'; label: string; story?: HelpStoryId } | { kind: 'field'; field: PreflightActionField }
    > => [
      ...(section.label ? [{ kind: 'heading' as const, label: section.label, story: section.story }] : []),
      ...section.fields.map((field) => ({ kind: 'field' as const, field })),
    ],
  );

  return (
    <Box sx={{ mb: 1.5 }}>
      {conflicts.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button size="small" color="inherit" onClick={takeTheirs}>
              Take theirs
            </Button>
          }
        >
          {conflicts.length === 1 && conflicts[0]
            ? `“${conflicts[0].label}” changed on the server while you were editing it.`
            : `${conflicts.length} fields changed on the server while you were editing them.`}{' '}
          Saving keeps your value.
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 2,
          alignItems: 'start',
        }}
      >
        {entries.map((entry) => {
          if (entry.kind === 'heading') {
            return (
              <Typography
                key={`section:${entry.label}`}
                variant="overline"
                component="h3"
                color="text.secondary"
                sx={{ gridColumn: '1 / -1', mt: 1, mb: -1, lineHeight: 1.5 }}
              >
                {entry.label}
                {entry.story && <HelpLink story={entry.story} newTab />}
              </Typography>
            );
          }
          const f = entry.field;
          const value = valueOf(f);
          const [label, suffix] = splitUnit(f);
          const defaultLabel = formatDefault(f);
          const atDefault = f.default != null && same(value, coerce(f, f.default));
          const chrome = {
            label,
            help: helpText(f),
            required: f.required,
            unfilled: f.required && isEmpty(value),
            missingWhy: whyByKey.get(f.key),
            error: errorOf(f),
            warning: fieldWarning(f, value),
            defaultLabel: defaultLabel || undefined,
            onRevert:
              defaultLabel && canManage && !saving && !atDefault
                ? () => setField(f.key, coerce(f, f.default))
                : undefined,
            // Restech keys are working credentials ops paste into vendor
            // dashboards. Masked by default so they stay off a shared screen;
            // revealable so nobody has to go to the Django admin for them.
            actions:
              f.secret && !isEmpty(value) && itemId != null ? (
                revealed[f.key] ? (
                  <Tooltip title={copied[f.key] ? 'Copied' : 'Copy'}>
                    <IconButton size="small" aria-label={`Copy ${label}`} onClick={() => copy(f)}>
                      {copied[f.key] ? (
                        <CheckIcon sx={{ fontSize: 14 }} color="success" />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Reveal — recorded in the audit log">
                    <IconButton
                      size="small"
                      aria-label={`Reveal ${label}`}
                      onClick={() => void reveal(f)}
                    >
                      <VisibilityIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )
              ) : undefined,
          };

          const widget = widgetControl(f, value, {
            topId,
            readOnly: !canManage || saving,
            onChange: (v) => setField(f.key, v),
          });

          return (
            <Box
              key={f.key}
              sx={{ minWidth: 0, gridColumn: isWide(f, value) || widget ? '1 / -1' : 'auto' }}
            >
              {widget ? (
                <FieldBlock {...chrome}>{widget}</FieldBlock>
              ) : (
                <InlineEditField
                  {...chrome}
                  type={f.type}
                  value={value}
                  suffix={suffix}
                  pending={dirtyKeys.includes(f.key)}
                  unsetDefault={isUnsetBool(f) && !dirtyKeys.includes(f.key)}
                  disabled={!canManage || saving}
                  options={toOptions(f)}
                  placeholder={
                    // An empty field with a default is already on that default — show
                    // the value itself, greyed, rather than the word "default".
                    f.type === 'bool' || f.type === 'list' ? undefined : defaultLabel || undefined
                  }
                  onChange={(v) => setField(f.key, v)}
                />
              )}
            </Box>
          );
        })}
      </Box>
      {canManage && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            disabled={dirtyKeys.length === 0 || blocking.length > 0 || saving}
            onClick={save}
          >
            Save values
          </Button>
          {dirtyKeys.length > 0 && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              disabled={saving}
              onClick={takeTheirs}
            >
              Discard
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
